// Locations modals module for Docospot
// Handles create/edit location modals and CRUD operations

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { apiCall } from '../core/api.js';
import { API_BASE_URL } from '../core/config.js';
import { showError, showSuccess } from '../core/ui.js';
import { loadLocationsData } from './locations.js';
import { initAccountSearch } from '../utils/account-search.js';
import { getCurrentUser } from '../utils/helpers.js';

let smartTimeUsers = [];
let smartTimeSearchTimeout = null;
// Tracks whether the user actually touched the SmartTime section in the
// current modal session. We only POST/PUT smart_time_users when this is true,
// so editing other fields (e.g. address) cannot silently wipe the list.
let smartTimeUsersDirty = false;

/**
 * Helper function to get alarm status icon for a sensor
 */
function getAlarmStatusIconForSensor(sensor) {
    const alarmInfo = sensor.alarm_status || {};
    const hasAlarms = alarmInfo.total_alarms > 0;
    
    if (!hasAlarms) {
        return ''; // No icon if no alarms
    }
    
    // Determine color and title based on status
    let colorClass, title;
    
    if (alarmInfo.suspended_count > 0) {
        // Yellow if any alarm is suspended
        colorClass = 'text-yellow-500';
        title = `${alarmInfo.suspended_count} alarm(s) suspended`;
    } else if (alarmInfo.active_count > 0) {
        // Green if has active alarms
        colorClass = 'text-green-500';
        title = `${alarmInfo.active_count} active alarm(s)`;
    } else {
        // Gray if all alarms are disabled
        colorClass = 'text-gray-400';
        title = `${alarmInfo.total_alarms} alarm(s) disabled`;
    }
    
    // Bell icon (outline style) - smaller for inline use
    return `
        <svg class="w-4 h-4 ${colorClass} mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" title="${title}">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
    `;
}

/**
 * Show location modal (create or edit)
 */
export async function showLocationModal(locationId = null) {
    const modal = document.getElementById('modal');
    if (!modal) return;
    
    let location = null;
    if (locationId) {
        // Fetch location details for edit
        try {
            const response = await fetch(`${API_BASE_URL}/locations/${locationId}`, {
                headers: { 'Authorization': `Bearer ${auth.token}` }
            });
            if (!response.ok) throw new Error('Failed to load location');
            location = await response.json();
        } catch (error) {
            showError('Kunne ikke indlæse lokation: ' + error.message);
            return;
        }
    }
    
    const isEdit = location !== null;
    const modalTitle = isEdit ? t('locations.edit') : t('locations.createTitle');

    modal.innerHTML = `
        <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div class="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
                <div class="mt-3">
                    <h3 class="text-lg font-medium text-gray-900 mb-4">${modalTitle}</h3>
                    <form id="location-form" class="space-y-4">
                        ${isEdit ? `<input type="hidden" id="location-id" value="${location.id}">` : ''}
                        <!-- Name and Account - Two Column -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Name -->
                            <div>
                                <label for="location-name" class="block text-sm font-medium text-gray-700">${t('locations.locationName')} <span class="text-red-500">*</span></label>
                                <input type="text" id="location-name" name="name" required value="${isEdit ? location.name : ''}"
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                            </div>
                            
                            <!-- Account -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">${t('locations.selectAccount')} <span class="text-red-500">*</span></label>
                                <!-- Search Field -->
                                <div class="relative mb-2">
                                    <input type="text" 
                                           id="location-account-search" 
                                           placeholder="${t('locations.searchAccountsPlaceholder')}" 
                                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                    <!-- Search Results Dropdown -->
                                    <div id="location-account-search-results" 
                                         class="hidden absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                                    </div>
                                </div>
                                <!-- Account Tree -->
                                <div id="location-account-tree-container" class="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-white">
                                    <div class="text-gray-400 text-sm">${t('locations.loadingAccounts')}</div>
                                </div>
                                <input type="hidden" id="location-account" name="account_id" required>
                            </div>
                        </div>
                        
                        <!-- Address and Coordinates - Two Column -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Address -->
                            <div>
                                <label for="location-address" class="block text-sm font-medium text-gray-700">${t('common.address')}</label>
                                <textarea id="location-address" name="address" rows="3"
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">${isEdit ? (location.address || '') : ''}</textarea>
                                <button type="button" 
                                        onclick="geocodeLocationAddress('${isEdit ? location.id : 'new'}')"
                                        class="mt-2 inline-flex items-center px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 transition-colors">
                                    <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    ${t('locations.geocodeAddress')}
                                </button>
                            </div>
                            
                            <!-- Coordinates -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">${t('locations.coordinates')}</label>
                                <div class="space-y-2">
                                    <div>
                                        <label class="block text-xs text-gray-600 mb-1">${t('locations.latitude')}</label>
                                        <input type="number" id="location-latitude" name="latitude" step="0.000001" placeholder="e.g., 56.26392" value="${isEdit ? (location.latitude || '') : ''}"
                                               class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500">
                                    </div>
                                    <div>
                                        <label class="block text-xs text-gray-600 mb-1">${t('locations.longitude')}</label>
                                        <input type="number" id="location-longitude" name="longitude" step="0.000001" placeholder="e.g., 9.501785" value="${isEdit ? (location.longitude || '') : ''}"
                                               class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500">
                                    </div>
                                </div>
                                <p class="mt-1 text-xs text-gray-500">${isEdit ? t('locations.autoFilledGeocoding') : t('locations.coordinatesHint')}</p>
                            </div>
                        </div>
                        
                        <!-- Sensors -->
                        <div class="border-t border-gray-200 pt-4">
                            <label class="block text-sm font-medium text-gray-700 mb-1">${t('locations.sensorsLabel')}</label>
                            <input type="text"
                                   id="location-sensor-search"
                                   placeholder="${t('common.search')} (ID / sensor)"
                                   autocomplete="off"
                                   oninput="(function(inp){var c=document.getElementById('location-sensors-container');if(!c)return;var q=(inp.value||'').trim().toLowerCase();c.querySelectorAll('[data-sensor-search]').forEach(function(r){var s=(r.getAttribute('data-sensor-search')||'').toLowerCase();if(q&&s.indexOf(q)===-1){r.classList.add('hidden');r.classList.remove('flex');}else{r.classList.remove('hidden');r.classList.add('flex');}});c.scrollTop=0;})(this)"
                                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 text-sm mb-2">
                            <div id="location-sensors-container" class="border border-gray-300 rounded-md p-3 max-h-[300px] overflow-y-auto bg-white">
                                <div class="text-gray-400 text-sm">${t('locations.loadingSensors')}</div>
                            </div>
                            <p class="mt-1 text-xs text-gray-500">${t('locations.selectSensorsHelp')}</p>
                        </div>
                        
                        <!-- External Contacts -->
                        <div class="border-t border-gray-200 pt-4">
                            <label class="block text-sm font-medium text-gray-700 mb-2">${t('locations.externalContacts')}</label>
                            <div id="location-contacts-container" class="border border-gray-300 rounded-md p-3 max-h-[200px] overflow-y-auto bg-white">
                                <div class="text-gray-400 text-sm">${t('locations.loadingUsers')}</div>
                            </div>
                            <p class="mt-1 text-xs text-gray-500">${t('locations.selectExternalContactsHelp')}</p>
                        </div>

                        <!-- SmartTime Users -->
                        <div id="location-smarttime-section" class="border-t border-gray-200 pt-4">
                            <label class="block text-sm font-medium text-gray-700 mb-2">${t('locations.smarttimeUsers')}</label>
                            <div class="relative mb-3">
                                <input type="text" 
                                       id="location-smarttime-user-search"
                                       placeholder="${t('locations.searchUsersToAdd')}"
                                       autocomplete="off"
                                       class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                <div id="location-smarttime-user-results" 
                                     class="hidden absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                                </div>
                            </div>
                            <div id="location-smarttime-users-selected" class="border border-gray-300 rounded-md p-3 bg-white">
                                <div class="text-gray-400 text-sm">${t('locations.noUsersAdded')}</div>
                            </div>
                            <div id="location-smarttime-validation"
                                 class="hidden mt-2 p-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md"
                                 role="alert"></div>
                            <p class="mt-1 text-xs text-gray-500">${t('locations.smarttimeDesc')}</p>
                        </div>
                        
                        <!-- Time Budget (Optional) -->
                        <div id="location-timebudget-section" class="border-t border-gray-200 pt-4">
                            <label class="block text-sm font-medium text-gray-700 mb-3">${t('locations.timeBudgetOptional')}</label>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <!-- Budget Period -->
                                <div>
                                    <label class="block text-sm text-gray-600 mb-2">${t('locations.budgetPeriod')}</label>
                                    <div class="space-y-2">
                                        <label class="flex items-center">
                                            <input type="radio" name="budget_period" value="weekly" ${isEdit && location.time_budget_period === 'weekly' ? 'checked' : (!isEdit ? 'checked' : '')}
                                                   class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300">
                                            <span class="ml-2 text-sm text-gray-700">${t('locations.weekly')}</span>
                                        </label>
                                        <label class="flex items-center">
                                            <input type="radio" name="budget_period" value="monthly" ${isEdit && location.time_budget_period === 'monthly' ? 'checked' : ''}
                                                   class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300">
                                            <span class="ml-2 text-sm text-gray-700">${t('locations.monthly')}</span>
                                        </label>
                                        <label class="flex items-center">
                                            <input type="radio" name="budget_period" value="daily" ${isEdit && location.time_budget_period === 'daily' ? 'checked' : ''}
                                                   class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300">
                                            <span class="ml-2 text-sm text-gray-700">${t('locations.daily')}</span>
                                        </label>
                                    </div>
                                </div>
                                
                                <!-- Budget Hours -->
                                <div>
                                    <label for="location-budget-hours" class="block text-sm text-gray-600 mb-2">${t('locations.budgetHours')}</label>
                                    <input type="number" id="location-budget-hours" name="budget_hours" min="0" step="0.5" placeholder="e.g., 40" value="${isEdit ? (location.time_budget_hours || '') : ''}"
                                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                    <p class="mt-1 text-xs text-gray-500">${t('locations.budgetHoursDesc')}</p>
                                </div>
                            </div>
                        </div>
                    </form>
                    <div class="flex items-center justify-end space-x-3 mt-6">
                        <button onclick="closeModal()" 
                                class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                            ${t('common.cancel')}
                        </button>
                        <button onclick="saveLocation()" 
                                class="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
                            ${isEdit ? t('locations.updateButton') : t('locations.createButton')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    modal.classList.remove('hidden');
    
    // Pre-populate account_id if editing
    if (isEdit && location.account_id) {
        const hiddenAccountInput = document.getElementById('location-account');
        if (hiddenAccountInput) {
            hiddenAccountInput.value = location.account_id;
            console.log('✅ Pre-populated account_id:', location.account_id);
        }
    }
    
    loadAccountsForLocationModal(isEdit ? location.account_id : null);
    // Pass account_id to load sensors from parent accounts too
    const locationAccountId = isEdit ? location.account_id : null;
    // Bind sensor search field immediately so typing works even before sensors finish loading
    attachLocationSensorSearch('location-sensor-search', 'location-sensors-container');
    loadAvailableSensorsForLocation(isEdit ? locationId : null, locationAccountId);
    loadAvailableContactUsersForLocation(isEdit ? locationId : null);
    await initSmartTimeUsers(locationId, locationAccountId, location?.smart_time_users || []);
}

/**
 * Save location (create or update based on presence of location ID)
 */
export async function saveLocation() {
    console.log('💾 saveLocation called');
    try {
        const form = document.getElementById('location-form');
        console.log('💾 Form element:', form);
        const formData = new FormData(form);
        const locationId = document.getElementById('location-id')?.value;
        const isEdit = !!locationId;
        console.log('💾 Location ID:', locationId, 'isEdit:', isEdit);
        
        const locationData = {
            name: formData.get('name'),
            address: formData.get('address') || '',
            account_id: formData.get('account_id'),
            latitude: formData.get('latitude') ? parseFloat(formData.get('latitude')) : null,
            longitude: formData.get('longitude') ? parseFloat(formData.get('longitude')) : null
        };
        console.log('💾 Basic location data collected:', locationData);
        
        // Collect selected sensors - always include when editing to handle removals
        const selectedSensors = Array.from(document.querySelectorAll('.location-sensor-checkbox:checked'))
            .map(cb => cb.value);
        console.log('💾 Selected sensors:', selectedSensors);
        if (isEdit || selectedSensors.length > 0) {
            locationData.sensor_ids = selectedSensors;
        }
        
        // Collect selected external users - always include when editing to handle removals
        const selectedContacts = Array.from(document.querySelectorAll('.location-contact-checkbox:checked'))
            .map(cb => cb.value);
        console.log('💾 Selected contacts:', selectedContacts);
        if (isEdit || selectedContacts.length > 0) {
            locationData.contact_user_ids = selectedContacts;
        }
        
        // Add time budget if provided
        const budgetHours = formData.get('budget_hours');
        console.log('💾 Budget hours from form:', budgetHours);
        if (budgetHours) {
            locationData.time_budget_hours = parseFloat(budgetHours);
            locationData.time_budget_period = formData.get('budget_period') || 'weekly';
        } else {
            // Explicitly clear budget if hours removed
            locationData.time_budget_hours = null;
            locationData.time_budget_period = null;
        }

        const invalidSmartTime = smartTimeUsers.find(item => !item.expected_daily_hours || item.expected_daily_hours <= 0);
        if (invalidSmartTime) {
            // Re-render so the offending rows get the red highlight + inline
            // banner, then scroll the first invalid row into view. Without this
            // the admin only saw a transient toast and could think the save
            // succeeded — the root cause of the lost SmartTime entries on
            // Rudersdal Kommune locations on 2026-04-29/30.
            renderSmartTimeUsers();
            const firstInvalidRow = document.querySelector('.smarttime-row-invalid');
            if (firstInvalidRow && typeof firstInvalidRow.scrollIntoView === 'function') {
                firstInvalidRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            showError(t('locations.smarttimeHoursRequired'));
            return;
        }

        // Only include smart_time_users in the payload when the admin actually
        // touched that section in this modal session, OR when creating a new
        // location with entries. This prevents an unrelated edit (e.g.
        // changing the address) from sending an empty array and silently
        // wiping previously-saved SmartTime entries.
        if (!isEdit && smartTimeUsers.length > 0) {
            locationData.smart_time_users = smartTimeUsers.map(item => ({
                user_id: item.user_id,
                expected_daily_hours: item.expected_daily_hours
            }));
        } else if (isEdit && smartTimeUsersDirty) {
            locationData.smart_time_users = smartTimeUsers.map(item => ({
                user_id: item.user_id,
                expected_daily_hours: item.expected_daily_hours
            }));
        }
        
        console.log('💾 Final locationData:', locationData);
        console.log('💾 Account ID check:', locationData.account_id);
        
        if (!locationData.account_id) {
            console.error('💾 No account_id - showing error');
            showError(t('locations.selectAccountRequired'));
            return;
        }
        console.log('💾 Account ID validation passed');
        
        const url = isEdit ? `/locations/${locationId}` : '/locations/';
        const method = isEdit ? 'PUT' : 'POST';
        console.log('💾 Saving location:', { url, method, locationData });
        
        const response = await apiCall(url, {
            method: method,
            headers: auth.getAuthHeaders(),
            body: JSON.stringify(locationData)
        });
        console.log('💾 Save response:', response);
        
        closeModal();
        showSuccess(isEdit ? t('locations.updateSuccess') : t('locations.createSuccess'));
        loadLocationsData(true);
    } catch (error) {
        showError(`${isEdit ? t('locations.updateError') : t('locations.createError')}: ` + (error.message || 'Ukendt fejl'));
        console.error('Error saving location:', error);
    }
}

/**
 * Load accounts tree for location modal
 */
async function loadAccountsForLocationModal(selectedAccountId = null) {
    try {
        const response = await apiCall('/accounts/hierarchy', {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        
        const container = document.getElementById('location-account-tree-container');
        if (!container) return;
        
        if (response && response.accounts_tree) {
            container.innerHTML = renderLocationAccountTree(response.accounts_tree, 0, selectedAccountId);
            
            // Initialize account search
            initAccountSearch(
                'location-account-search',
                'location-account-search-results',
                'location-account-tree-container',
                'location-account'
            );
            
            // If an account is pre-selected, check the radio button
            if (selectedAccountId) {
                const radioButton = document.querySelector(`input[name="location-account-selection"][value="${selectedAccountId}"]`);
                if (radioButton) {
                    radioButton.checked = true;
                    console.log('✅ Pre-checked account radio button for:', selectedAccountId);
                }
            }
        } else {
            container.innerHTML = `<div class="text-gray-500 text-sm">${t('locations.noAccountsFound')}</div>`;
        }
            
    } catch (error) {
        console.error('Error loading accounts:', error);
    }
}

/**
 * Render account tree for create modal
 */
function renderLocationAccountTree(accounts, level = 0, selectedAccountId = null) {
    let html = '';
    
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 20;
        const isSelected = selectedAccountId === accountId;
        
        html += `
            <div class="account-tree-item" data-level="${level}">
                <div class="flex items-center py-1.5 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="toggleLocationAccountTreeItem('loc-tree-${accountId}')" 
                                class="mr-1 w-4 h-4 flex items-center justify-center flex-shrink-0 text-gray-400 hover:text-gray-600">
                            <svg id="loc-tree-icon-${accountId}" class="w-4 h-4 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="mr-1 w-4 h-4 flex-shrink-0"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="radio" name="location-account-selection" value="${accountId}" 
                               ${isSelected ? 'checked' : ''}
                               onchange="selectLocationAccount('${accountId}')"
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="loc-tree-${accountId}" class="hidden">
                        ${renderLocationAccountTree(account.children, level + 1, selectedAccountId)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    
    return html;
}

/**
 * Toggle account tree item
 */
export function toggleLocationAccountTreeItem(itemId) {
    const item = document.getElementById(itemId);
    const iconId = itemId.replace('loc-tree-', 'loc-tree-icon-');
    const icon = document.getElementById(iconId);
    
    if (item && icon) {
        item.classList.toggle('hidden');
        icon.classList.toggle('rotate-90');
    }
}

/**
 * Select account in create modal
 */
export function selectLocationAccount(accountId) {
    const hiddenInput = document.getElementById('location-account');
    if (hiddenInput) {
        hiddenInput.value = accountId;
    }
    
    // Reload sensors to show sensors from selected account + parent accounts
    const locationId = document.getElementById('location-id')?.value;
    loadAvailableSensorsForLocation(locationId || null, accountId);
    updateSmartTimeAccountContext(accountId);
}

/**
 * Load available sensors for location modal (create or edit)
 */
async function loadAvailableSensorsForLocation(locationId = null, accountId = null) {
    const container = document.getElementById('location-sensors-container');
    if (!container) return;
    
    try {
        container.innerHTML = `<p class="text-sm text-gray-500">${t('locations.loadingSensors')}</p>`;
        
        const pageSize = 500;
        let allSensors = [];
        let skip = 0;
        let hasMore = true;
        
        // Get existing sensors if editing (sensors have location_id, not location having sensor_ids)
        let existingSensors = [];
        let existingSensorIds = [];
        if (locationId) {
            try {
                // Query sensors that have this location_id (use location_ids parameter - plural!)
                const sensorsResponse = await fetch(`${API_BASE_URL}/sensors?location_ids=${locationId}&limit=1000`, {
                    headers: { 'Authorization': `Bearer ${auth.token}` }
                });
                if (sensorsResponse.ok) {
                    const sensorsData = await sensorsResponse.json();
                    existingSensors = sensorsData.sensors || [];
                    existingSensorIds = existingSensors.map(s => s.id);
                    console.log('📍 Existing sensors for location:', existingSensors);
                    console.log('📍 Existing sensor IDs:', existingSensorIds);
                }
            } catch (error) {
                console.error('Failed to load existing sensors:', error);
            }
        }
        
        while (hasMore) {
            const params = new URLSearchParams();
            params.append('limit', pageSize.toString());
            params.append('skip', skip.toString());
            // Always get unassociated sensors, we'll add current location's sensors separately
            params.append('unassociated_only', 'true');
            // Include sensors from parent accounts (upward hierarchy)
            if (accountId) {
                params.append('account_id', accountId);
                params.append('include_parent_sensors', 'true');
            }
            
            const response = await fetch(`${API_BASE_URL}/sensors?${params}`, {
                headers: {
                    'Authorization': `Bearer ${auth.token}`
                }
            });
            
            if (!response.ok) {
                throw new Error(`Failed to load sensors: ${response.status}`);
            }
            
            const data = await response.json();
            const sensors = data.sensors || [];
            
            allSensors = allSensors.concat(sensors);
            
            if (sensors.length < pageSize || allSensors.length >= data.total) {
                hasMore = false;
            } else {
                skip += pageSize;
                container.innerHTML = `<p class="text-sm text-gray-500">${t('locations.loadingSensors')} (${allSensors.length} af ${data.total})</p>`;
            }
        }
        
        // If editing, add the existing sensors to the list (already fetched above)
        if (locationId && existingSensors.length > 0) {
            console.log('🔍 Adding existing sensors to list:', existingSensors);
            for (const sensor of existingSensors) {
                // Add to list if not already present
                if (!allSensors.find(s => s.id === sensor.id)) {
                    allSensors.push(sensor);
                    console.log(`✅ Added existing sensor ${sensor.id} to list`);
                }
            }
            console.log('🔍 Final allSensors count:', allSensors.length);
        }
        
        if (allSensors.length > 0) {
            const html = `
                <div class="space-y-1">
                    ${allSensors.map(sensor => {
                        const isChecked = existingSensorIds.includes(sensor.id);
                        const alarmIcon = getAlarmStatusIconForSensor(sensor);
                        const searchText = `${sensor.sensor_id || ''} ${sensor.name || ''}`.toLowerCase();
                        return `
                            <div class="flex items-center py-1" data-sensor-search="${searchText.replace(/"/g, '&quot;')}">
                                <input type="checkbox" 
                                       id="sensor-${sensor.id}" 
                                       value="${sensor.id}"
                                       ${isChecked ? 'checked' : ''}
                                       class="location-sensor-checkbox h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                <label for="sensor-${sensor.id}" class="ml-2 text-sm text-gray-700 cursor-pointer flex-grow flex items-center">
                                    <code class="bg-gray-100 px-1 py-0.5 rounded text-xs mr-1">${sensor.sensor_id}</code>
                                    ${alarmIcon}
                                    <span>${sensor.name}</span>
                                </label>
                                ${isChecked ? `
                                    <button type="button"
                                            onclick="showEditSensorModal('${sensor.id}', '${locationId}')"
                                            class="ml-2 px-2 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded hover:bg-primary-200"
                                            title="Manage alarms for this sensor">
                                        Edit Alarm
                                    </button>
                                ` : ''}
                            </div>
                        `;
                    }).join('')}
                </div>
            `;
            container.innerHTML = html;
            // Wire up the search field (idempotent) and re-apply any active filter
            attachLocationSensorSearch('location-sensor-search', 'location-sensors-container');
        } else {
            container.innerHTML = '<p class="text-sm text-gray-500">No available sensors to associate</p>';
        }
        
    } catch (error) {
        console.error('Error loading available sensors:', error);
        container.innerHTML = `<p class="text-sm text-red-500">Kunne ikke indlæse sensorer: ${error.message}</p>`;
    }
}

/**
 * Load available external contact users for location modal (create or edit)
 */
async function loadAvailableContactUsersForLocation(locationId = null) {
    const container = document.getElementById('location-contacts-container');
    if (!container) return;
    
    try {
        container.innerHTML = `<p class="text-sm text-gray-500">${t('locations.loadingUsers')}</p>`;
        console.log('👥 Loading external users for location:', locationId);
        
        // Get existing contact IDs and location's account if editing
        let existingContactIds = [];
        let locationAccountId = null;
        
        if (locationId) {
            const locationResponse = await fetch(`${API_BASE_URL}/locations/${locationId}`, {
                headers: { 'Authorization': `Bearer ${auth.token}` }
            });
            if (locationResponse.ok) {
                const locationData = await locationResponse.json();
                existingContactIds = (locationData.contact_user_ids || []).map(u => typeof u === 'object' ? u.id : u);
                locationAccountId = locationData.account_id;
                console.log('👥 Location account ID:', locationAccountId);
                console.log('👥 Existing contact IDs:', existingContactIds);
            }
        }
        
        // Use location's account if editing, otherwise use current user's account
        const currentUser = getCurrentUser();
        const accountId = locationAccountId || currentUser?.account_id;
        console.log('👥 Using account ID:', accountId);
        
        if (!accountId) {
            console.error('👥 No account ID available');
            container.innerHTML = '<p class="text-sm text-gray-500">Unable to load users</p>';
            return;
        }

        // Fetch users using correct parameter names (match working Users list request)
        console.log('👥 Fetching users...');
        
        const response = await fetch(`${API_BASE_URL}/users/?account_id=${accountId}&include_child_accounts=true&limit=500`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${auth.token}`,
                'Content-Type': 'application/json'
            }
        });
        
        console.log('👥 Response status:', response.status);
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('👥 API response - total users:', data.users?.length);
        const allUsers = data.users || [];
        
        // Filter to External contact role in frontend
        const externalUsers = allUsers.filter(user => user.role === 'External contact');
        console.log('👥 External contact users count:', externalUsers.length);
        
        if (externalUsers.length > 0) {
            const html = `
                <div class="space-y-1">
                    ${externalUsers.map(user => {
                        const isChecked = existingContactIds.includes(user.id);
                        return `
                            <div class="flex items-center py-1">
                                <input type="checkbox" 
                                       id="contact-${user.id}" 
                                       value="${user.id}"
                                       ${isChecked ? 'checked' : ''}
                                       class="location-contact-checkbox h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                <label for="contact-${user.id}" class="ml-2 text-sm text-gray-700 cursor-pointer">
                                    ${user.full_name || user.username}
                                    ${user.phone_number ? `<span class="text-gray-500 text-xs ml-1">(${user.phone_number})</span>` : ''}
                                </label>
                            </div>
                        `;
                    }).join('')}
                </div>
            `;
            container.innerHTML = html;
        } else {
            container.innerHTML = '<p class="text-sm text-gray-500">No external contact users available</p>';
        }
        
    } catch (error) {
        console.error('Error loading external users:', error);
        container.innerHTML = `<p class="text-sm text-red-500">Failed to load users: ${error.message}</p>`;
    }
}

/**
 * Close modal
 */
function closeModal() {
    const modal = document.getElementById('modal');
    if (modal) {
        modal.classList.add('hidden');
        modal.innerHTML = '';
    }
}

/**
 * Filter sensor checkboxes inside a container by search query (matches sensor_id or name)
 */
function filterLocationSensorList(input, containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;
    const query = (input?.value || '').trim().toLowerCase();
    const rows = container.querySelectorAll('[data-sensor-search]');
    rows.forEach(row => {
        const text = row.getAttribute('data-sensor-search') || '';
        const match = !query || text.includes(query);
        if (match) {
            row.classList.remove('hidden');
            row.style.display = '';
        } else {
            row.classList.add('hidden');
            row.style.display = 'none';
        }
    });
    // Reset scroll so matches are visible from the top
    container.scrollTop = 0;
}

/**
 * Attach (idempotent) an input listener on the sensor search field that filters the list,
 * and re-apply the current filter after a list re-render.
 */
function attachLocationSensorSearch(inputId, containerId) {
    const input = document.getElementById(inputId);
    if (!input) return;
    if (!input.dataset.searchBound) {
        input.addEventListener('input', () => filterLocationSensorList(input, containerId));
        input.dataset.searchBound = '1';
    }
    if (input.value) {
        filterLocationSensorList(input, containerId);
    }
}

// Make functions globally accessible for onclick handlers
if (typeof window !== 'undefined') {
    window.closeModal = closeModal;
    window.showLocationModal = showLocationModal;
    window.showCreateLocationModal = () => showLocationModal(); // Backward compatibility
    window.saveLocation = saveLocation;
    window.toggleLocationAccountTreeItem = toggleLocationAccountTreeItem;
    window.selectLocationAccount = selectLocationAccount;
    window.refreshLocationModalSensors = refreshLocationModalSensors;
    window.addSmartTimeUser = addSmartTimeUser;
    window.removeSmartTimeUser = removeSmartTimeUser;
    window.updateSmartTimeHours = updateSmartTimeHours;
    window.filterLocationSensorList = filterLocationSensorList;
}

function updateSmartTimeAccountContext(accountId) {
    const searchInput = document.getElementById('location-smarttime-user-search');
    if (searchInput) {
        searchInput.dataset.accountId = accountId || '';
    }
    applyLocationFeatureFlags();
}

// Monotonic guard so only the most recent feature-flag lookup applies (avoids
// flicker when the account context changes several times while the modal opens).
let featureFlagRequestSeq = 0;

/**
 * Hide optional Location features (SmartTime, Time Budget) when the selected
 * account's governing top-level account has turned them off. Always reads the
 * currently selected account from the hidden input so a stale/empty call can
 * never override a real selection. Shows everything while no account is chosen
 * or if the lookup fails (backward compatible).
 */
async function applyLocationFeatureFlags() {
    const accountId = document.getElementById('location-account')?.value || null;

    const setVisible = (el, visible) => {
        if (el) el.classList.toggle('hidden', !visible);
    };
    const applyDisabled = (disabled) => {
        const smartTimeSection = document.getElementById('location-smarttime-section');
        const timeBudgetSection = document.getElementById('location-timebudget-section');
        const timeTrackingOff = disabled.includes('timetracking');
        setVisible(smartTimeSection, !timeTrackingOff && !disabled.includes('smarttime'));
        setVisible(timeBudgetSection, !timeTrackingOff && !disabled.includes('time_budget'));
    };

    const seq = ++featureFlagRequestSeq;

    // 1) Instant, flicker-free default from the logged-in user's governing
    //    top-level account (correct for every non-SuperAdmin; SuperAdmin resolves
    //    to the empty root set and is refined by the per-account lookup below).
    const permDisabled = (window.permissions && Array.isArray(window.permissions.disabledFeatures))
        ? window.permissions.disabledFeatures
        : [];
    applyDisabled(permDisabled);

    // No account chosen yet -> keep the permission-based default
    if (!accountId) return;

    // 2) Refine against the actually-selected account (covers SuperAdmin and
    //    managing accounts outside the user's own top account).
    try {
        const resp = await apiCall(`/accounts/${accountId}/features`, {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        if (seq !== featureFlagRequestSeq) return; // a newer request superseded this one
        applyDisabled((resp && resp.disabled_features) || []);
    } catch (error) {
        if (seq !== featureFlagRequestSeq) return;
        console.warn('Could not resolve location feature flags, keeping default:', error);
    }
}

async function initSmartTimeUsers(locationId, accountIdOverride, existingSmartTime) {
    smartTimeUsers = [];
    smartTimeUsersDirty = false;
    updateSmartTimeAccountContext(accountIdOverride);
    const selectedContainer = document.getElementById('location-smarttime-users-selected');
    if (selectedContainer) {
        selectedContainer.innerHTML = '<div class="text-gray-400 text-sm">No users added</div>';
    }

    if (existingSmartTime && existingSmartTime.length > 0) {
        smartTimeUsers = existingSmartTime.map(entry => ({
            user_id: entry.user_id,
            expected_daily_hours: entry.expected_daily_hours,
            user_name: 'Loading...'
        }));

        await Promise.all(smartTimeUsers.map(async entry => {
            try {
                const userResponse = await fetch(`${API_BASE_URL}/users/${entry.user_id}`, {
                    headers: { 'Authorization': `Bearer ${auth.token}` }
                });
                if (userResponse.ok) {
                    const userData = await userResponse.json();
                    entry.user_name = userData.full_name || userData.username || entry.user_id;
                } else {
                    entry.user_name = entry.user_id;
                }
            } catch (error) {
                entry.user_name = entry.user_id;
            }
        }));

        renderSmartTimeUsers();
    }

    setupSmartTimeUserSearch(accountIdOverride, locationId);
}

function renderSmartTimeUsers() {
    const container = document.getElementById('location-smarttime-users-selected');
    if (!container) return;

    if (!smartTimeUsers.length) {
        container.innerHTML = '<div class="text-gray-400 text-sm">No users added</div>';
        updateSmartTimeValidationBanner();
        return;
    }

    container.innerHTML = `
        <div class="space-y-2">
            ${smartTimeUsers.map(user => {
                const hours = user.expected_daily_hours;
                const isInvalid = !hours || hours <= 0;
                const inputClasses = `w-28 rounded-md shadow-sm sm:text-sm ${isInvalid
                    ? 'border-red-500 ring-red-500 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'}`;
                return `
                <div class="flex items-center justify-between gap-3 py-1 ${isInvalid ? 'smarttime-row-invalid' : ''}"
                     data-smarttime-row="${user.user_id}">
                    <div class="flex-1">
                        <div class="text-sm text-gray-800">${user.user_name || user.user_id}</div>
                        ${isInvalid ? `<div class="text-xs text-red-600 mt-0.5">${t('locations.smarttimeHoursRequired') || 'Daily hours required'}</div>` : ''}
                    </div>
                    <div class="flex items-center gap-2">
                        <input type="number"
                               min="0.5"
                               step="0.25"
                               value="${user.expected_daily_hours ?? ''}"
                               placeholder="hrs/day"
                               oninput="updateSmartTimeHours('${user.user_id}', this.value)"
                               class="${inputClasses}">
                        <span class="text-xs text-gray-500">hours/day</span>
                        <button type="button"
                                onclick="removeSmartTimeUser('${user.user_id}')"
                                class="ml-1 text-red-600 hover:text-red-800 text-sm">
                            Remove
                        </button>
                    </div>
                </div>`;
            }).join('')}
        </div>
    `;
    updateSmartTimeValidationBanner();
}

/**
 * Show or hide an inline warning above the SmartTime list when one or more
 * rows are missing the required hours value. This makes the validation state
 * obvious before the admin clicks Save (where previously they would see only
 * a transient toast and could easily miss it).
 */
function updateSmartTimeValidationBanner() {
    const banner = document.getElementById('location-smarttime-validation');
    if (!banner) return;
    const invalidCount = smartTimeUsers.filter(u => !u.expected_daily_hours || u.expected_daily_hours <= 0).length;
    if (invalidCount === 0) {
        banner.classList.add('hidden');
        banner.textContent = '';
        return;
    }
    banner.classList.remove('hidden');
    const msg = t('locations.smarttimeHoursRequired') || 'Daily hours are required for every SmartTime user';
    banner.textContent = invalidCount === 1
        ? msg
        : `${msg} (${invalidCount})`;
}

function setupSmartTimeUserSearch(accountIdOverride, locationId) {
    const searchInput = document.getElementById('location-smarttime-user-search');
    const resultsDiv = document.getElementById('location-smarttime-user-results');
    if (!searchInput || !resultsDiv) return;

    const currentUser = getCurrentUser();
    const accountId = accountIdOverride || searchInput.dataset.accountId || currentUser?.account_id || '';
    searchInput.dataset.accountId = accountId;

    searchInput.addEventListener('input', (e) => {
        const query = e.target.value.trim();
        clearTimeout(smartTimeSearchTimeout);

        if (query.length < 2) {
            resultsDiv.classList.add('hidden');
            return;
        }

        smartTimeSearchTimeout = setTimeout(async () => {
            try {
                resultsDiv.innerHTML = '<div class="p-2 text-gray-500">Searching...</div>';
                resultsDiv.classList.remove('hidden');

                const params = new URLSearchParams();
                params.append('search', query);
                params.append('limit', '20');
                params.append('include_parent_accounts', 'true');

                const response = await fetch(`${API_BASE_URL}/users/?${params.toString()}`, {
                    headers: { 'Authorization': `Bearer ${auth.getToken()}` }
                });

                if (!response.ok) throw new Error('Search failed');

                const data = await response.json();
                const users = (data.users || []).filter(user => user.role !== 'External contact');

                if (users.length === 0) {
                    resultsDiv.innerHTML = '<div class="p-2 text-gray-500">No users found</div>';
                } else {
                    resultsDiv.innerHTML = users.map(user => {
                        const userId = user.id || user._id;
                        const displayName = user.full_name || user.username;
                        return `
                            <div class="p-2 hover:bg-gray-100 cursor-pointer" 
                                 onclick="addSmartTimeUser('${userId}', '${displayName.replace(/'/g, "\\'")}')">
                                ${displayName}${user.email ? ` (${user.email})` : ''}
                            </div>
                        `;
                    }).join('');
                }
            } catch (error) {
                console.error('SmartTime user search error:', error);
                resultsDiv.innerHTML = '<div class="p-2 text-red-500">Error searching users</div>';
            }
        }, 300);
    });

    document.addEventListener('click', function(e) {
        if (!searchInput.contains(e.target) && !resultsDiv.contains(e.target)) {
            resultsDiv.classList.add('hidden');
        }
    });
}

function addSmartTimeUser(userId, userName) {
    const exists = smartTimeUsers.some(u => u.user_id === userId);
    if (exists) return;

    smartTimeUsers.push({
        user_id: userId,
        user_name: userName,
        expected_daily_hours: null
    });
    smartTimeUsersDirty = true;

    const searchInput = document.getElementById('location-smarttime-user-search');
    const resultsDiv = document.getElementById('location-smarttime-user-results');
    if (searchInput) searchInput.value = '';
    if (resultsDiv) resultsDiv.classList.add('hidden');

    renderSmartTimeUsers();
}

function removeSmartTimeUser(userId) {
    smartTimeUsers = smartTimeUsers.filter(u => u.user_id !== userId);
    smartTimeUsersDirty = true;
    renderSmartTimeUsers();
}

function updateSmartTimeHours(userId, value) {
    const entry = smartTimeUsers.find(u => u.user_id === userId);
    if (!entry) return;
    const parsed = value ? parseFloat(value) : null;
    entry.expected_daily_hours = Number.isNaN(parsed) ? null : parsed;
    smartTimeUsersDirty = true;
    // Update only this row's invalid styling so the input keeps focus.
    const row = document.querySelector(`[data-smarttime-row="${userId}"]`);
    if (row) {
        const isInvalid = !entry.expected_daily_hours || entry.expected_daily_hours <= 0;
        row.classList.toggle('smarttime-row-invalid', isInvalid);
        const input = row.querySelector('input[type="number"]');
        if (input) {
            input.classList.toggle('border-red-500', isInvalid);
            input.classList.toggle('ring-red-500', isInvalid);
        }
    }
    updateSmartTimeValidationBanner();
}

/**
 * Refresh the sensors list in the location modal (called after sensor alarm edit)
 */
async function refreshLocationModalSensors() {
    // Get the current location ID from the modal
    const locationId = document.getElementById('location-id')?.value;
    const accountId = document.getElementById('location-account')?.value;
    if (!locationId) return;
    
    console.log('🔄 Refreshing sensors for location:', locationId);
    await loadAvailableSensorsForLocation(locationId, accountId);
}

export default {
    showLocationModal,
    showCreateLocationModal: () => showLocationModal(), // Backward compatibility
    saveLocation,
    toggleLocationAccountTreeItem,
    selectLocationAccount
};
